import { useCallback, useEffect, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';

import { ComponentLoader } from 'components';
import fetch from 'features/common/fetch';
import { gtmDashboardSectionNames, gtmFilterNames } from 'features/views/dashboard/constants';
import { useButtonClickDataLayer, useDetailButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { fetchLeadsOverview } from 'features/views/dashboard/leadsOverview/actions';
import { toggleButtonGroupValues } from 'features/views/dashboard/leadsOverview/constants';
import { getLeadsOverview, getToggleValue } from 'features/views/dashboard/leadsOverview/selectors';
import { setToggleValue } from 'features/views/dashboard/leadsOverview/slice';

import Lead from './components/Lead';

export const LeadsOverview = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickDetailButtonDataLayer = useDetailButtonClickDataLayer();
	const pushToClickButtonDataLayer = useButtonClickDataLayer();

	const leadsOverview = useSelector(getLeadsOverview);
	const toggleValue = useSelector(getToggleValue);

	useEffect(() => {
		dispatch(fetchLeadsOverview());
	}, [dispatch]);

	const handleRedirect = useCallback(() => {
		const filterType =
			toggleValue === toggleButtonGroupValues.CURRENT ? gtmFilterNames.CURRENT : gtmFilterNames.DRAFT;

		pushToClickDetailButtonDataLayer({
			value: gtmDashboardSectionNames.LEADS,
			filter_type: filterType,
		})();

		navigate('/market-place');
	}, [navigate, toggleValue, pushToClickDetailButtonDataLayer]);

	const handleToggle = useCallback(
		(e, toggleValue) => {
			if (toggleValue !== null) {
				const buttonText =
					toggleValue === toggleButtonGroupValues.DRAFT ? gtmFilterNames.DRAFT : gtmFilterNames.CURRENT;

				pushToClickButtonDataLayer({
					value: gtmDashboardSectionNames.LEADS,
					button_text: buttonText,
				})();

				dispatch(setToggleValue(toggleValue));
				dispatch(fetchLeadsOverview());
			}
		},
		[dispatch, pushToClickButtonDataLayer],
	);

	const selectedButtonSx = {
		'&.Mui-selected, &.Mui-selected:hover': {
			background: (theme) => theme.palette.primary.main,
			color: (theme) => theme.palette.common.white,
		},
	};

	const noResults = useMemo(
		() => (
			<Alert severity="info">
				{getMessage(
					`dashboard:leads.${
						toggleValue === toggleButtonGroupValues.CURRENT ? 'noCurrentLeads' : 'noDraftLeads'
					}`,
				)}
			</Alert>
		),
		[toggleValue],
	);

	return (
		<Paper sx={{ p: 2, borderRadius: 1 }}>
			<Stack spacing={2}>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Typography sx={{ fontWeight: 'bold' }} variant="body1">
						{getMessage('dashboard:leads.title')}
					</Typography>
					<Button
						sx={{ width: 'max-content', justifyContent: 'left' }}
						variant="text"
						endIcon={<ArrowRight />}
						onClick={handleRedirect}
					>
						{getMessage('dashboard:detail')}
					</Button>
				</Stack>

				<ComponentLoader fetchStorePath={fetch.constants.urls.leadsOverview} forceInlineError>
					<Stack spacing={2}>
						<ToggleButtonGroup
							size="small"
							exclusive
							value={toggleValue}
							onChange={handleToggle}
							sx={{
								mb: 2,
								width: 'fit-content',
								height: '30px',
							}}
						>
							<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.CURRENT}>
								{getMessage('dashboard:leads.current')}
							</ToggleButton>
							<ToggleButton sx={selectedButtonSx} value={toggleButtonGroupValues.DRAFT}>
								{getMessage('dashboard:leads.draft')}
							</ToggleButton>
						</ToggleButtonGroup>
						<Stack spacing={2}>
							{isNilOrEmpty(leadsOverview)
								? noResults
								: leadsOverview.map((leadType) => <Lead key={leadType?.id} lead={leadType} />)}
						</Stack>
					</Stack>
				</ComponentLoader>
			</Stack>
		</Paper>
	);
};

export default LeadsOverview;
